import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Tabs,
  Badge,
  Button,
  Skeleton,
  Input,
  Select,
  Toggle,
  FormField,
  Modal,
  DataTable,
  Timeline,
  ConfirmDialog,
  FileUpload,
  DatePicker,
  Tooltip,
  StockLevelIndicator,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, TimelineItem } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { productMasterService } from '@/services/product-master.service';
import { TRACKING_OPTIONS, TRACKING_LABELS } from '@/types/product';
import type { TrackingMode, TrackingOverride, SerialStatus } from '@/types/product';
import { CategoryCombobox } from '@/components/CategoryCombobox';
import { BarcodeScanField } from '@/components/BarcodeScanField';
import { uploadProductImage } from './product-image-upload';
import { MAX_RAW_UPLOAD_BYTES } from '@/lib/upload-file';
import { ProductGalleryPanel } from '@/components/features/products/ProductGalleryPanel';
import {
  MasterStoresPanel,
  masterQueryKey,
  useManageableStore,
} from '@/components/features/products/MasterStoresPanel';
import { merchandisingService } from '@/services/product-image.service';
import { locationService } from '@/services/location.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { buildTaxSlabOptions, UNIT_OPTIONS, ROLE_LEVELS } from '@/constants/product';
import { GLASS, GLASS_FORM, cn, toneClasses } from '@/constants/design-tokens';
import { formatPaise, formatNumber } from '@/utils/currency';
import { paiseToRupeeInput, rupeeInputToPaise } from '@/utils/price-input';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { LockedFeature } from '@/components/features/subscriptions/LockedFeature';
import type { UpdateProductPayload, LocationStock, StockHistoryItem, StockAdjustmentType } from '@/types/product';
import { variantAttributeService, VARIANT_ATTRIBUTES_QUERY_KEY } from '@/services/variant-attribute.service';
import { VariantMatrixBuilder } from '@/components/products/VariantMatrixBuilder';
import {
  type VariantRow,
  type AttributeDef,
  normalizedCombinationKey,
  findDuplicateCombinations,
  buildCreateVariantsPayload,
} from './product-variant-payload';
import { toProductUpdate } from './products/product-update-payload';
import { VariantStockPanel } from './products/VariantStockPanel';
import { activeVariantRange, formatRange } from './products/variant-kpi';
import { VariantPricingTable } from './products/VariantPricingTable';
import { ProductChangeLog } from './products/ProductChangeLog';
import { editInfoLine } from './products/change-log-format';
import { stockMovementTimelineItem } from './products/stock-movement-timeline';
import type { VariantPriceUpdate } from './products/variant-pricing-edits';

// ─── Icons ──────────────────────────────────────────────────────────────────

function KpiIcon({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// ─── Stock columns ──────────────────────────────────────────────────────────

const stockColumns: ColumnDef<LocationStock & { id: string }>[] = [
  {
    key: 'location_name',
    header: 'Location',
    render: (_v, row) => <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.location_name}</span>,
  },
  {
    key: 'stock_quantity',
    header: 'Stock',
    numeric: true,
    render: (_v, row) => <span className="text-sm tabular-nums text-neutral-700 dark:text-neutral-300">{formatNumber(row.stock_quantity)}</span>,
  },
  {
    key: 'low_stock_threshold',
    header: 'Threshold',
    numeric: true,
    render: (_v, row) => <span className="text-sm tabular-nums text-neutral-500">{row.low_stock_threshold}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => (
      <StockLevelIndicator
        currentStock={row.stock_quantity}
        reorderLevel={row.low_stock_threshold}
        variant="badge"
        size="sm"
      />
    ),
  },
];

// ─── KPI Card (matches PlatformDashboardContent pattern) ─────���──────────────

function ProductKpiCard({
  label,
  value,
  iconPath,
  tone,
  caption,
}: {
  label: string;
  value: string;
  iconPath: string;
  tone: 'teal' | 'brand' | 'warning' | 'info';
  caption?: string;
}) {
  const palette = toneClasses(tone);

  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-5 transition-all duration-200 hover:shadow-lg')}>
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          <KpiIcon d={iconPath} />
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
            {value}
          </p>
          {caption && <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{caption}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Section Header ─────────────────────────────────────────────────────────

function SectionHeader({ title, tone }: { title: string; tone: 'teal' | 'brand' | 'violet' | 'warning' | 'info' }) {
  const palette = toneClasses(tone);
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className={cn('h-5 w-1 rounded-full', palette.icon.replace('bg-', 'bg-').split(' ')[0])} />
      <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">{title}</h3>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

function ProductDetailContent() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const profile = useAuthStore((s) => s.profile);
  const businessType = profile?.tenant?.business_type ?? 'RETAIL';
  const isRestaurant = businessType === 'RESTAURANT';
  const userRoleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;

  const canDelete = userRoleLevel >= ROLE_LEVELS.OWNER;

  const activeTab = searchParams.get('tab') ?? 'details';
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showStockAdjust, setShowStockAdjust] = useState(false);
  const [showVariantMatrix, setShowVariantMatrix] = useState(false);

  // "Add variants" builder state — RETAIL only. Attribute names are locked to
  // the existing variants' set once the product has any; values can be added.
  const [variantAttrs, setVariantAttrs] = useState<AttributeDef[]>([]);
  const [variantRows, setVariantRows] = useState<VariantRow[]>([]);
  const [variantError, setVariantError] = useState('');

  // Edit form
  const [formData, setFormData] = useState<UpdateProductPayload>({});
  const [formDirty, setFormDirty] = useState(false);
  // Prices typed into the Variants table but not saved yet.
  const [variantPricesDirty, setVariantPricesDirty] = useState(false);
  const [variantPricesSaving, setVariantPricesSaving] = useState(false);
  // Leaving with unsaved edits asks first (links, Esc, the variant Edit button).
  const { confirmLeave, dialog: leaveDialog } = useUnsavedChangesGuard(formDirty || variantPricesDirty);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Price inputs are typed in rupees; formData keeps the canonical paise value.
  const [priceRupees, setPriceRupees] = useState('');
  const [costRupees, setCostRupees] = useState('');

  // Stock adjustment
  const [adjustQty, setAdjustQty] = useState('');
  // 'COUNT' is the UI-only "Set counted quantity" mode (QA IS1-003): sent as an ADJUSTMENT with counted_quantity.
  const [adjustType, setAdjustType] = useState<StockAdjustmentType | 'COUNT' | ''>('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustUnitCost, setAdjustUnitCost] = useState('');
  const [adjustBatchId, setAdjustBatchId] = useState('');
  const [adjustLocationId, setAdjustLocationId] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const isWriteOffType = adjustType === 'DAMAGE' || adjustType === 'WASTAGE' || adjustType === 'EXPIRY_WRITE_OFF';

  const isCount = adjustType === 'COUNT';
  const ADJUSTMENT_TYPE_OPTIONS: { label: string; value: StockAdjustmentType | 'COUNT' }[] = [
    { label: 'Set Counted Quantity (Physical Count)', value: 'COUNT' },
    { label: 'Purchase (Stock In)', value: 'PURCHASE' },
    { label: 'Manual Adjustment', value: 'ADJUSTMENT' },
    { label: 'Opening Stock', value: 'OPENING_STOCK' },
    { label: 'Damage Write-off', value: 'DAMAGE' },
    { label: 'Wastage', value: 'WASTAGE' },
    { label: 'Expiry Write-off', value: 'EXPIRY_WRITE_OFF' },
  ];

  const TABS = [
    { id: 'details', label: 'Details' },
    { id: 'stock', label: 'Stock' },
    // Which stores sell it, and each store's own price (master catalogue plan §11).
    { id: 'stores', label: 'Stores' },
    // POS comments item 4: this tab only ever listed stock movements.
    { id: 'history', label: 'Stock movements' },
    { id: businessType === 'RESTAURANT' ? 'recipe' : 'variants', label: businessType === 'RESTAURANT' ? 'Recipe' : 'Variants' },
    // Everything that only affects the public storefront. Kept off the Details
    // tab because none of it changes what the till sells or charges.
    { id: 'website', label: 'Website' },
  ];

  // Queries
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getById(id!),
    enabled: Boolean(id),
  });

  // Variants of this product (incl. inactive), listed on the Variants tab so
  // each can be opened and edited; also feeds the "Add variants" builder with
  // the locked attribute names and the combinations that already exist. Only
  // parents have children, so skip the call for a variant row. Loaded on every
  // tab: the Current Stock card rolls the variants up, and must read the same
  // on every tab (CS4-006).
  const variantsQuery = useQuery({
    queryKey: ['product', id, 'variants'],
    queryFn: () => productService.getVariants(id!, true),
    enabled: Boolean(id) && !product?.parent_product_id,
  });
  const variantList = variantsQuery.data?.variants ?? [];

  const { data: variantLibrary } = useQuery({
    queryKey: VARIANT_ATTRIBUTES_QUERY_KEY,
    queryFn: () => variantAttributeService.list(),
    staleTime: 60_000,
    enabled: activeTab === 'variants' && !isRestaurant,
  });

  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey: ['product', id, 'stock'],
    queryFn: () => productService.getStock(id!),
    enabled: Boolean(id) && (activeTab === 'stock' || activeTab === 'details'),
  });

  // Open batches for the write-off batch picker (Phase 4) — only fetched while
  // the adjust modal is open for a stock-decreasing type.
  const { data: openBatches } = useQuery({
    queryKey: ['product', id, 'batches'],
    queryFn: () => productService.getBatches(id!),
    enabled: Boolean(id) && showStockAdjust && isWriteOffType,
  });

  // Serial numbers captured on goods receipts — only for SERIAL-tracked products.
  const effectiveTracking = ((product as unknown as { effective_tracking?: TrackingMode } | undefined)?.effective_tracking) ?? null;
  const hasBatchSerial = usePlanFeature('batch_serial');
  const [serialStatus, setSerialStatus] = useState<SerialStatus | ''>('');
  const { data: serialsData, isLoading: serialsLoading, isError: serialsError } = useQuery({
    queryKey: ['product', id, 'serials', serialStatus],
    queryFn: () => productService.getSerials(id!, serialStatus ? { status: serialStatus } : undefined),
    enabled: Boolean(id) && activeTab === 'serials' && hasBatchSerial,
  });

  // "Created … by … · Last updated … by …" and the Change log tab (Manager+).
  const canSeeEditHistory = userRoleLevel >= ROLE_LEVELS.MANAGER;
  const { data: editInfo } = useQuery({
    queryKey: ['product', id, 'edit-info'],
    queryFn: () => productService.getEditInfo(id!),
    enabled: Boolean(id) && canSeeEditHistory,
  });

  // This row's movements — the row is one store, the one the header names.
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['product', id, 'stock-history'],
    queryFn: () => productService.getStockHistory(id!, { limit: 50 }),
    enabled: Boolean(id) && activeTab === 'history',
  });

  const { data: locData } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
  });

  // The master's store rows, for the store switcher: the user's own stores
  // that carry this product (CS4-006). Shares the Stores tab's cache.
  const canManageStore = useManageableStore();
  const { data: master } = useQuery({
    queryKey: masterQueryKey(product?.master_id ?? ''),
    queryFn: () => productMasterService.getById(product!.master_id!),
    enabled: Boolean(product?.master_id),
  });
  const storeRows = (master?.stores ?? []).filter(
    (s): s is typeof s & { product_id: string } => Boolean(s.product_id) && canManageStore(s.location_id),
  );

  const locations = locData?.data ?? [];
  // An owner switches a master-catalogue product on / off at every store (the
  // master endpoint, CS3-007); a manager only at the store row this page opened.
  const masterWide = canDelete && Boolean(product?.master_id);
  const storeName =
    locations.find((l) => l.id === product?.location_id)?.name ??
    master?.stores.find((s) => s.location_id === product?.location_id)?.location_name ??
    'this store';
  const _locationOptions = locations.map((l) => ({ value: l.id, label: l.name }));

  // Populate form from product
  useEffect(() => {
    if (product && !formDirty) {
      setFormData({
        name: product.name,
        sku: product.sku,
        barcode: product.barcode ?? '',
        category: product.category ?? '',
        unit: product.unit,
        price: product.price,
        cost: product.cost,
        gst_rate: product.gst_rate as 0 | 5 | 12 | 18 | 28,
        purchase_gst_rate: ((product as unknown as Record<string, unknown>).purchase_gst_rate as 0 | 5 | 12 | 18 | 28 | null) ?? undefined,
        tracking: ((product as unknown as Record<string, unknown>).tracking as TrackingOverride | null) ?? 'DEFAULT',
        commission_pct: ((product as unknown as Record<string, unknown>).commission_pct as number | null) ?? undefined,
        hsn_code: product.hsn_code ?? '',
        low_stock_threshold: product.low_stock_threshold,
        is_active: product.is_active,
        hide_in_pos: product.hide_in_pos ?? false,
        hide_in_sales: product.hide_in_sales ?? false,
        description: ((product as unknown as Record<string, unknown>).description as string) ?? '',
        expiry_date: product.expiry_date ?? '',
      });
      setPriceRupees(paiseToRupeeInput(product.price, currency));
      setCostRupees(paiseToRupeeInput(product.cost, currency));
    }
  }, [product, formDirty, currency]);

  // Arriving from a variant's "Save & add another" (?addVariants=1): open the
  // matrix for them. Deferred until the variants query resolves, because
  // openVariantMatrix seeds the locked attributes from it — fire early and the
  // user gets the blank "pick attributes" flow instead of "add more
  // combinations of Size x Colour". The param is consumed so a refresh or a
  // back-navigation doesn't reopen it.
  const autoOpenedMatrix = useRef(false);
  useEffect(() => {
    if (autoOpenedMatrix.current) return;
    if (searchParams.get('addVariants') !== '1') return;
    if (!variantsQuery.isSuccess) return;
    autoOpenedMatrix.current = true;
    openVariantMatrix();
    const next = new URLSearchParams(searchParams);
    next.delete('addVariants');
    setSearchParams(next, { replace: true });
    // openVariantMatrix is deliberately not a dependency: it is recreated every
    // render, and the ref above already makes this fire exactly once.
  }, [searchParams, setSearchParams, variantsQuery.isSuccess]);

  // Keyboard: Ctrl+S = save, Esc = back
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (formDirty) handleSave();
      }
      // Esc inside a dialog closes that dialog — it must not also leave the page
      // (or re-open the unsaved-changes dialog it just dismissed).
      if (e.key === 'Escape' && !(e.target as Element | null)?.closest?.('[aria-modal="true"]')) {
        confirmLeave(() => navigate('/products'));
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateProductPayload) => {
      const { is_active, ...rest } = data;
      const toMaster = masterWide && is_active !== undefined && is_active !== product?.is_active;
      const saved = await productService.update(id!, toMaster ? rest : data);
      if (toMaster) await productMasterService.update(product!.master_id!, { is_active });
      return saved;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-masters'] });
      setFormDirty(false);
      toast({ title: 'Product saved', variant: 'success' });
    },
    onError: (err: Error) => {
      const msg = err.message;
      // Barcode first: its refusal names the holder's SKU.
      if (/barcode/i.test(msg)) setFormErrors((prev) => ({ ...prev, barcode: msg }));
      else if (msg.includes('SKU')) setFormErrors((prev) => ({ ...prev, sku: msg }));
      else toast({ title: msg ?? 'Failed to save product', variant: 'error' });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (): Promise<unknown> =>
      masterWide
        ? productMasterService.update(product!.master_id!, { is_active: false })
        : productService.update(id!, { is_active: false }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-masters'] });
      setShowDeactivate(false);
      const n = (res as { variants_affected?: number } | undefined)?.variants_affected ?? 0;
      toast({
        title: n > 0 ? `Product deactivated with ${n} variant${n === 1 ? '' : 's'}` : 'Product deactivated',
        variant: 'success',
      });
    },
    onError: (err: Error) => {
      toast({ title: err.message ?? 'Failed to deactivate product', variant: 'error' });
    },
  });

  // An inactive product is switched back on, never deleted (OD8); the API
  // refuses it at the plan's product limit (OD12) and the toast says so.
  const reactivateMutation = useMutation({
    mutationFn: (): Promise<unknown> =>
      masterWide
        ? productMasterService.update(product!.master_id!, { is_active: true })
        : productService.update(id!, { is_active: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-masters'] });
      toast({ title: 'Product reactivated', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: err.message ?? 'Failed to reactivate product', variant: 'error' });
    },
  });

  // Save the prices and costs typed into the Variants table, one variant at a
  // time; a failure stops there and names the variant.
  async function saveVariantPrices(updates: VariantPriceUpdate[]) {
    setVariantPricesSaving(true);
    let saved = 0;
    try {
      for (const { id: variantId, ...change } of updates) {
        await productService.update(variantId, change);
        saved++;
      }
      toast({ title: `Prices saved for ${saved} variant${saved === 1 ? '' : 's'}`, variant: 'success' });
    } catch (err) {
      const name = variantList.find((v) => v.id === updates[saved]?.id)?.name ?? 'a variant';
      toast({ title: `${saved} saved; ${name} failed: ${(err as Error).message}`, variant: 'error' });
      throw err;
    } finally {
      setVariantPricesSaving(false);
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  }

  // Per-variant on/off: hides that combination (e.g. XL / Red) from the POS
  // picker and catalog without deleting it — reversible, history intact.
  const variantActiveMutation = useMutation({
    mutationFn: ({ variantId, is_active }: { variantId: string; is_active: boolean }) =>
      productService.update(variantId, { is_active }),
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({ queryKey: ['product', id, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({ title: vars.is_active ? 'Variant activated' : 'Variant deactivated', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: err.message ?? 'Failed to update variant', variant: 'error' });
    },
  });

  // One request creates every enabled row atomically (all or nothing).
  const variantCreateMutation = useMutation({
    mutationFn: () => productService.createVariants(id!, buildCreateVariantsPayload(variantRows, currency)),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', id, 'variants'] });
      // New names/values typed in the builder were added to the library on save.
      queryClient.invalidateQueries({ queryKey: VARIANT_ATTRIBUTES_QUERY_KEY });
      setShowVariantMatrix(false);
      setVariantAttrs([]);
      setVariantRows([]);
      setVariantError('');
      toast({ title: `${created.length} variants created`, variant: 'success' });
    },
    onError: (err: Error) => {
      // 409s name the clashing SKU/barcode/combination — keep the modal open to fix it.
      setVariantError(err.message ?? 'Failed to create variants');
    },
  });

  const stockAdjustMutation = useMutation({
    mutationFn: () =>
      productService.adjustStock(id!, {
        ...(isCount
          ? { type: 'ADJUSTMENT' as const, counted_quantity: Number(adjustQty) }
          : { type: adjustType as StockAdjustmentType, quantity: Number(adjustQty) }),
        ...(adjustType === 'PURCHASE' && adjustUnitCost ? { unit_cost: rupeeInputToPaise(adjustUnitCost, currency) ?? 0 } : {}),
        ...(adjustNotes ? { notes: adjustNotes } : {}),
        ...(isWriteOffType && adjustBatchId ? { batch_id: adjustBatchId } : {}),
        ...(adjustLocationId ? { location_id: adjustLocationId } : {}),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      setShowStockAdjust(false);
      setAdjustQty('');
      setAdjustType('');
      setAdjustNotes('');
      setAdjustUnitCost('');
      setAdjustBatchId('');
      setAdjustLocationId('');
      setAdjustError('');
      if (res.pending_approval) {
        // QA IS4-002: above the wastage limit the write-off waits in Approvals.
        toast({ title: 'Sent for approval — above your wastage limit. The stock is held until it is approved.', variant: 'warning' });
        return;
      }
      // A count equal to the system stock books nothing (QA IS1-003).
      toast({ title: res.adjustment ? 'Stock adjusted' : 'Count matches the system stock — nothing to adjust', variant: 'success' });
    },
    onError: (err: Error) => {
      // e.g. 400 "Insufficient stock" on a write-off — keep the dialog open and say why.
      setAdjustError(err.message || 'Failed to adjust stock');
    },
  });

  function updateField(key: string, value: unknown) {
    setFormDirty(true);
    setFormErrors((prev) => ({ ...prev, [key]: '' }));
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  // Existing variants lock the attribute names; the builder pre-fills each
  // with the values already in use (plus the library's) so only new
  // combinations get generated.
  const lockedAttributeNames = (variantsQuery.data?.attributes ?? []).map((a) => a.name);
  const existingCombinationKeys = variantList.map((v) =>
    normalizedCombinationKey((v.variant_attributes ?? {}) as Record<string, string>),
  );

  function openVariantMatrix() {
    setVariantError('');
    setVariantRows([]);
    setVariantAttrs(
      (variantsQuery.data?.attributes ?? []).map((a, i) => ({ id: `locked-${i}`, name: a.name, values: a.values })),
    );
    setShowVariantMatrix(true);
  }

  function handleVariantSave() {
    const payload = buildCreateVariantsPayload(variantRows, currency);
    if (payload.variants.length === 0) {
      setVariantError('Generate at least one variant first');
      return;
    }
    const dupes = findDuplicateCombinations(variantRows);
    if (dupes.length > 0) {
      setVariantError(`Duplicate variant combination: ${dupes.join(', ')}`);
      return;
    }
    setVariantError('');
    variantCreateMutation.mutate();
  }

  function handleSave() {
    const errs: Record<string, string> = {};
    if (!formData.name || formData.name.length < 2) errs.name = 'Name is required';
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // Variants inherit Show-in-POS/Sales from the parent — never send those fields for them.
    if (product?.parent_product_id) {
      const { hide_in_pos: _hp, hide_in_sales: _hs, ...rest } = formData;
      updateMutation.mutate(toProductUpdate(rest));
      return;
    }
    updateMutation.mutate(toProductUpdate(formData));
  }

  // Loading state
  if (isLoading) {
    return (
      <PageContainer title="Product Details" breadcrumbs={[{ label: 'Products', href: '/products' }, { label: '…' }]}>
        <div className="space-y-4">
          <Skeleton variant="rect" height={80} />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={120} />
            ))}
          </div>
          <Skeleton variant="rect" height={400} />
        </div>
      </PageContainer>
    );
  }

  // Error / not found
  if (error || !product) {
    toast({ title: 'Product not found', variant: 'error' });
    navigate('/products');
    return null;
  }

  // A variant parent holds no stock itself — the units sit on its children —
  // so its own row is 0 and the KPI would read "Out of Stock" for a
  // product with plenty on hand. Roll the active variants up instead. Both
  // are this store's row, on every tab (the Stock tab lists all stores).
  const ownStock = product.stock_quantity;
  const variantStock = variantList
    .filter((v) => v.is_active)
    .reduce((sum, v) => sum + (v.stock_quantity ?? 0), 0);
  const totalStock = variantList.length > 0 ? variantStock : ownStock;
  // A parent's own price and cost are usually 0 — the variants carry them.
  const priceRange = activeVariantRange(variantList, 'price');
  const costRange = activeVariantRange(variantList, 'cost');
  const variantCaption = (n: number) => `across ${n} variant${n === 1 ? '' : 's'}`;

  // Timeline items badged with the movement type
  const historyTimeline: TimelineItem[] = (historyData?.data ?? []).map((h: StockHistoryItem) =>
    stockMovementTimelineItem(h, product),
  );

  return (
    <PageContainer
      breadcrumbs={[{ label: 'Products', href: '/products' }, { label: product.name }]}
      subtitle={editInfo ? editInfoLine(editInfo) : undefined}
      title={
        // Status sits right beside the name so it reads at a glance.
        <span className="inline-flex flex-wrap items-center gap-2">
          <span>{product.name}</span>
          <Badge variant={product.is_active ? 'success' : 'warning'} dot>
            {product.is_active ? 'Active' : 'Inactive'}
          </Badge>
          {product.hide_in_pos && <Badge variant="neutral" size="sm">Hidden in POS</Badge>}
          {product.hide_in_sales && <Badge variant="neutral" size="sm">Hidden in Sales</Badge>}
          {/* The store row this page shows and edits (CS4-006). */}
          <Badge variant="brand" size="sm">Store: {storeName}</Badge>
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          {storeRows.length > 1 && (
            <div className="w-48" title="Switch store">
              <Select
                id="prod-store"
                options={storeRows.map((s) => ({ value: s.product_id, label: s.location_name }))}
                value={product.id}
                onChange={(v) => {
                  if (typeof v === 'string' && v && v !== product.id) {
                    navigate(`/products/${v}${activeTab === 'details' ? '' : `?tab=${activeTab}`}`);
                  }
                }}
              />
            </div>
          )}
          {/* Save reads its state at a glance: grey until something changes, dark once it can be saved. */}
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!formDirty || updateMutation.isPending}
            title={formDirty ? 'Save changes (Ctrl+S)' : 'No changes to save'}
            className={cn(
              '!opacity-100 transition-colors duration-200',
              formDirty
                ? 'shadow-md' // dirty → the default primary (brand blue), same as "+ Add Product"
                : '!bg-neutral-200 !text-neutral-500 !border-transparent shadow-none dark:!bg-neutral-700 dark:!text-neutral-400',
            )}
          >
            {updateMutation.isPending ? 'Saving…' : formDirty ? 'Save changes' : 'Save'}
          </Button>
          {/* No hard delete (OD8): an active product is deactivated, an inactive one reactivated. */}
          {product.is_active ? (
            <Button variant="secondary" size="sm" onClick={() => setShowDeactivate(true)}>
              Deactivate
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => reactivateMutation.mutate()}
              loading={reactivateMutation.isPending}
              className="!text-success-700 !border-success-200 hover:!bg-success-50 dark:!text-success-400 dark:!border-success-800 dark:hover:!bg-success-900/20"
            >
              Reactivate
            </Button>
          )}
        </div>
      }
    >
      {/* ─── KPI Stat Cards (Inventory domain = teal palette) ──── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <ProductKpiCard
          label="Current Stock"
          value={`${formatNumber(totalStock)} ${product.unit}`}
          caption={
            Number(product.held_quantity ?? 0) > 0
              // QA IS4-002: on hand, but not sellable until the wastage is reviewed.
              ? `at ${storeName} · ${formatNumber(Number(product.held_quantity))} held for wastage approval`
              : `at ${storeName}`
          }
          iconPath="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
          tone="teal"
        />
        <ProductKpiCard
          label="Selling Price"
          value={priceRange ? formatRange(priceRange, (m) => formatPaise(m, currency)) : formatPaise(product.price, currency)}
          caption={priceRange ? variantCaption(priceRange.count) : undefined}
          iconPath="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          tone="brand"
        />
        <ProductKpiCard
          label="Cost (WAC)"
          value={costRange ? formatRange(costRange, (m) => formatPaise(m, currency)) : formatPaise(product.cost, currency)}
          caption={costRange ? variantCaption(costRange.count) : undefined}
          iconPath="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
          tone="warning"
        />
        <ProductKpiCard
          label={`${tax.taxName} Rate`}
          value={`${product.gst_rate}%`}
          iconPath="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          tone="info"
        />
      </div>

      {/* ─── Tabs ─────────────────────────────────────────────────── */}
      <Tabs
        items={[
          ...TABS,
          ...(canSeeEditHistory ? [{ id: 'changes', label: 'Change log' }] : []),
          ...(effectiveTracking === 'SERIAL' ? [{ id: 'serials', label: 'Serials' }] : []),
        ]}
        activeId={activeTab}
        onChange={(tabId) => {
          const params = new URLSearchParams(searchParams);
          params.set('tab', tabId);
          setSearchParams(params);
        }}
      />

      <div className="mt-6">
        {/* ─── Details Tab ─── */}
        {activeTab === 'details' && (
          <div className={cn(GLASS_FORM, 'p-6')}>
            {/* Product image — prominent at top */}
            <div className="mb-6">
              <SectionHeader title="Product Image" tone="violet" />
              <div className="flex items-start gap-6">
                <div className="shrink-0 h-32 w-32 rounded-xl overflow-hidden bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/10">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <KpiIcon d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-10 w-10 text-teal-300 dark:text-teal-700" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <FileUpload
                    accept="image/*"
                    maxSize={MAX_RAW_UPLOAD_BYTES}
                    onUpload={async (files) => {
                      if (!files.length) return;
                      try {
                        const key = await uploadProductImage(files[0]);
                        updateMutation.mutate({ image_url: key });
                      } catch {
                        toast({ title: 'Image upload failed', variant: 'error' });
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Identity section */}
            <SectionHeader title="Identity" tone="teal" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Names are owned by the parent: a variant's name is derived as
                  "<parent name> (Blue / 5S)" and rebuilt whenever the parent is
                  renamed, so editing it here would be silently overwritten. */}
              <FormField
                label="Product Name"
                htmlFor="prod-name"
                required={!product.parent_product_id}
                error={formErrors.name}
                helperText={product.parent_product_id
                  ? 'Set on the parent product — renaming it updates every variant.'
                  : undefined}
              >
                <Input
                  id="prod-name"
                  value={formData.name ?? ''}
                  onChange={(v) => updateField('name', v)}
                  disabled={!!product.parent_product_id}
                />
              </FormField>
              <FormField label="SKU" htmlFor="prod-sku" error={formErrors.sku} helperText="Fixed once created — a different SKU means a new product">
                <Tooltip content="Stock Keeping Unit — unique product identifier">
                  <Input id="prod-sku" value={formData.sku ?? ''} onChange={(v) => updateField('sku', v)} disabled />
                </Tooltip>
              </FormField>
              <FormField label="Barcode" htmlFor="prod-barcode" error={formErrors.barcode}>
                <BarcodeScanField
                  id="prod-barcode"
                  value={formData.barcode ?? ''}
                  onChange={(v) => updateField('barcode', v)}
                />
              </FormField>
              <FormField label="Category" htmlFor="prod-category">
                <CategoryCombobox
                  id="prod-category"
                  value={formData.category ?? ''}
                  onChange={(v) => updateField('category', v)}
                />
              </FormField>
            </div>

            {/* Pricing section */}
            <SectionHeader title="Pricing & Tax" tone="brand" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* POS comments item 3: once a product has variants it is priced per
                  variant — the parent's own price/cost would only conflict. */}
              {priceRange ? (
                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50 p-4 dark:border-violet-800 dark:from-violet-900/20 dark:to-indigo-900/20" data-testid="priced-per-variant">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-violet-800 dark:text-violet-200">Priced per variant</p>
                    <p className="text-xs text-violet-700 dark:text-violet-300 tabular-nums">
                      Selling price {formatRange(priceRange, (m) => formatPaise(m, currency))} · Cost{' '}
                      {costRange ? formatRange(costRange, (m) => formatPaise(m, currency)) : '—'} · {variantCaption(priceRange.count)}
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => setSearchParams({ tab: 'variants' })}>
                    Set prices on the Variants tab
                  </Button>
                </div>
              ) : (
              <>
              <FormField label={`Selling Price (${getCurrencySymbol(currency)})`} htmlFor="prod-price">
                <Input
                  id="prod-price"
                  type="number"
                  variant="currency"
                  currency={currency}
                  placeholder="e.g. 399"
                  value={priceRupees}
                  onChange={(v) => {
                    setPriceRupees(v);
                    updateField('price', rupeeInputToPaise(v, currency) ?? 0);
                  }}
                />
              </FormField>
              <FormField label={`Cost Price (${getCurrencySymbol(currency)})`} htmlFor="prod-cost">
                <Input
                  id="prod-cost"
                  type="number"
                  variant="currency"
                  currency={currency}
                  placeholder="e.g. 140"
                  value={costRupees}
                  onChange={(v) => {
                    setCostRupees(v);
                    updateField('cost', rupeeInputToPaise(v, currency) ?? 0);
                  }}
                />
              </FormField>
              {!isRestaurant && !product.parent_product_id && (
                <div className="md:col-span-2 -mt-1">
                  <Button variant="ghost" size="sm" onClick={() => setSearchParams({ tab: 'variants', addVariants: '1' })}>
                    + Add variants (sizes, colours…)
                  </Button>
                </div>
              )}
              </>
              )}
              <FormField label={`Sales ${tax.taxName} (on selling price)`} htmlFor="prod-gst">
                <Select
                  id="prod-gst"
                  options={buildTaxSlabOptions(tax.taxName, tax.regime, product?.gst_rate)}
                  value={String(formData.gst_rate ?? 0)}
                  onChange={(v) => updateField('gst_rate', Number(v))}
                />
              </FormField>
              <FormField label={`Purchase ${tax.taxName} (on cost price)`} htmlFor="prod-purchase-gst">
                <Select
                  id="prod-purchase-gst"
                  options={buildTaxSlabOptions(tax.taxName, tax.regime, (product as unknown as Record<string, unknown> | undefined)?.purchase_gst_rate as number | null | undefined ?? product?.gst_rate)}
                  value={String(formData.purchase_gst_rate ?? formData.gst_rate ?? 0)}
                  onChange={(v) => updateField('purchase_gst_rate', Number(v))}
                />
              </FormField>
              <FormField
                label="Tracking on receipt"
                htmlFor="prod-tracking"
                helperText={`Currently: ${effectiveTracking ? TRACKING_LABELS[effectiveTracking] : '—'}. Untracked unless enabled here or tenant-wide in Settings → Inventory Tracking.`}
              >
                <Select
                  id="prod-tracking"
                  options={TRACKING_OPTIONS}
                  value={formData.tracking ?? 'DEFAULT'}
                  onChange={(v) => updateField('tracking', (typeof v === 'string' ? v : 'DEFAULT') as TrackingOverride)}
                />
              </FormField>
              {/* Per-item commission — retail only. */}
              {!isRestaurant && (
                <FormField label="Commission (%)" htmlFor="prod-commission">
                  <Input
                    id="prod-commission"
                    type="number"
                    value={formData.commission_pct != null ? String(formData.commission_pct) : ''}
                    onChange={(v) => updateField('commission_pct', v === '' ? undefined : Number(v))}
                    placeholder="e.g. 5"
                  />
                </FormField>
              )}
              {tax.showHsn && (
                <FormField label="HSN/SAC Code" htmlFor="prod-hsn">
                  <Tooltip content="Harmonized System of Nomenclature code for GST classification">
                    <Input id="prod-hsn" value={formData.hsn_code ?? ''} onChange={(v) => updateField('hsn_code', v)} />
                  </Tooltip>
                </FormField>
              )}
            </div>

            {/* Inventory section */}
            <SectionHeader title="Inventory Settings" tone="warning" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <FormField label="Unit of Measure" htmlFor="prod-unit" helperText="Fixed once created — a different unit means a new product">
                <Select
                  id="prod-unit"
                  options={UNIT_OPTIONS as unknown as { value: string; label: string }[]}
                  value={formData.unit ?? 'pcs'}
                  onChange={() => undefined}
                  disabled
                />
              </FormField>
              <FormField label="Min Stock (low-stock alert)" htmlFor="prod-threshold">
                <Input
                  id="prod-threshold"
                  type="number"
                  value={String(formData.low_stock_threshold ?? 0)}
                  onChange={(v) => updateField('low_stock_threshold', Number(v))}
                />
              </FormField>
              <FormField label="Expiry Date" htmlFor="prod-expiry">
                <DatePicker
                  value={formData.expiry_date ? new Date(formData.expiry_date) : undefined}
                  onChange={(date) => updateField('expiry_date', date.toISOString().slice(0, 10))}
                  placeholder="Select expiry date"
                  minDate={new Date()}
                />
              </FormField>
              <div className="sm:col-span-2 rounded-xl border border-neutral-200 bg-white/60 p-4 dark:border-neutral-700 dark:bg-neutral-900/40">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Availability</p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Toggle
                    checked={formData.is_active ?? true}
                    onChange={(v) => updateField('is_active', v)}
                    label="Active"
                    description={!masterWide
                      ? `Off stops selling this ${product.parent_product_id ? 'variant' : 'product and all its variants'} at ${storeName} only.`
                      : product.parent_product_id
                        ? 'Off hides just this variant, at every store.'
                        : 'Off hides this product and all its variants everywhere.'}
                  />
                  {product.parent_product_id ? (
                    <p className="sm:col-span-2 self-center text-xs text-neutral-500 dark:text-neutral-400">
                      Show in POS / Sales is set on the parent product — this variant inherits it
                      {product.hide_in_pos ? ' (currently hidden in POS)' : ''}{product.hide_in_sales ? ' (currently hidden in Sales)' : ''}.
                    </p>
                  ) : (
                    <>
                      <Toggle
                        checked={!(formData.hide_in_pos ?? false)}
                        onChange={(v) => updateField('hide_in_pos', !v)}
                        label="Show in POS"
                        description="Counter billing & restaurant ordering."
                      />
                      <Toggle
                        checked={!(formData.hide_in_sales ?? false)}
                        onChange={(v) => updateField('hide_in_sales', !v)}
                        label="Show in Sales"
                        description="Quotations & sales orders."
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Stock Tab ─── */}
        {activeTab === 'stock' && variantList.length > 0 && (
          <VariantStockPanel parentId={id!} unit={product.unit} />
        )}
        {activeTab === 'stock' && variantList.length === 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowStockAdjust(true)}>
                Adjust Stock
              </Button>
              <Button variant="secondary" size="sm" onClick={() => toast({ title: 'Stock transfer available in Inventory module', variant: 'info' })}>
                Transfer Stock
              </Button>
            </div>

            {stockLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} variant="rect" height={56} />
                ))}
              </div>
            ) : (
              <DataTable<LocationStock & { id: string }>
                columns={stockColumns}
                data={(stockData ?? []).map((s) => ({ ...s, id: s.location_id }))}
                emptyState={{
                  title: 'No stock data',
                  description: 'Stock levels will appear once inventory is initialized',
                }}
              />
            )}
          </div>
        )}

        {/* ─── Stores Tab ─── */}
        {activeTab === 'stores' && (
          product.master_id ? (
            <MasterStoresPanel masterId={product.master_id} />
          ) : (
            <div className={cn(GLASS, 'p-6 text-center text-sm text-neutral-500 dark:text-neutral-400')}>
              This product is not in the master catalogue yet.
            </div>
          )
        )}

        {/* ─── Serials Tab ─── */}
        {activeTab === 'changes' && canSeeEditHistory && (
          <ProductChangeLog productId={id!} productName={product.name} currency={currency} />
        )}

        {activeTab === 'serials' && !hasBatchSerial && <LockedFeature feature="batch_serial" />}

        {activeTab === 'serials' && hasBatchSerial && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Serial numbers captured on goods receipts. Selling by serial at POS is coming next — units stay <span className="font-medium">In stock</span> until then.
              </p>
              <div className="w-48">
                <Select
                  id="serial-status"
                  options={[
                    { value: '', label: 'All statuses' },
                    { value: 'IN_STOCK', label: 'In stock' },
                    { value: 'SOLD', label: 'Sold' },
                    { value: 'RETURNED', label: 'Returned' },
                    { value: 'WRITTEN_OFF', label: 'Written off' },
                  ]}
                  value={serialStatus}
                  onChange={(v) => setSerialStatus((typeof v === 'string' ? v : '') as SerialStatus | '')}
                />
              </div>
            </div>
            {serialsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} variant="rect" height={48} />
                ))}
              </div>
            ) : serialsError ? (
              <div className={cn(GLASS_FORM, 'p-8 text-center')} role="alert">
                <p className="font-medium text-danger-700 dark:text-danger-300">Could not load serial numbers</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Reload the page to try again.</p>
              </div>
            ) : (serialsData ?? []).length === 0 ? (
              <div className={cn(GLASS_FORM, 'p-8 text-center')}>
                <p className="font-medium text-neutral-700 dark:text-neutral-200">No serial numbers yet</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">They are captured when goods are received against a purchase order.</p>
              </div>
            ) : (
              <div className={cn(GLASS_FORM, 'overflow-x-auto')}>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                      <th className="px-4 py-2.5">Serial number</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5">Location</th>
                      <th className="px-4 py-2.5">Received</th>
                      <th className="px-4 py-2.5">Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serialsData ?? []).map((s) => (
                      <tr key={s.id} className="even:bg-neutral-50 dark:even:bg-neutral-800/40 hover:bg-brand-50 dark:hover:bg-neutral-700/40 transition-colors border-b border-neutral-100 dark:border-neutral-800 animate-slide-up">
                        <td className="px-4 py-2.5 font-mono font-semibold text-neutral-900 dark:text-white">{s.serial_number}</td>
                        <td className="px-4 py-2.5">
                          <span className={cn(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            s.status === 'IN_STOCK' && 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300',
                            s.status === 'SOLD' && 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
                            s.status === 'RETURNED' && 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
                            s.status === 'WRITTEN_OFF' && 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
                          )}>
                            {s.status === 'IN_STOCK' ? 'In stock' : s.status === 'WRITTEN_OFF' ? 'Written off' : s.status.charAt(0) + s.status.slice(1).toLowerCase()}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-neutral-700 dark:text-neutral-300">{s.location_name ?? '—'}</td>
                        <td className="px-4 py-2.5 text-neutral-700 dark:text-neutral-300 tabular-nums">{new Date(s.received_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td className="px-4 py-2.5 text-neutral-700 dark:text-neutral-300">
                          {s.purchase_order_id ? (
                            <Link to={`/purchase-orders/${s.purchase_order_id}`} className="text-brand-600 hover:underline">{s.grn_number ?? s.po_number ?? 'View PO'}</Link>
                          ) : (s.grn_number ?? '—')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── History Tab ─── */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Stock movements at {storeName}</p>
            {historyLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} variant="rect" height={48} />
                ))}
              </div>
            ) : historyTimeline.length > 0 ? (
              <Timeline items={historyTimeline} />
            ) : (
              <div className={cn(GLASS, 'p-8 text-center')}>
                <div className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 mb-3">
                  <KpiIcon d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="h-7 w-7" />
                </div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">No stock movements yet</p>
                <p className="text-xs text-neutral-500 mt-1">Stock movement history will appear here once changes are made</p>
              </div>
            )}
          </div>
        )}

        {/* ─── Variants / Recipe Tab ─── */}
        {(activeTab === 'variants' || activeTab === 'recipe') && (
          <div className={cn(GLASS_FORM, 'p-6')}>
            {businessType === 'RESTAURANT' ? (
              <div className="text-center py-6">
                <div className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400 mb-4">
                  <KpiIcon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" className="h-7 w-7" />
                </div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">Recipe & Ingredient Breakdown</p>
                <p className="text-xs text-neutral-500 max-w-sm mx-auto">
                  Recipe and ingredient breakdown will be available after Recipe module integration.
                </p>
              </div>
            ) : product.is_variant || product.parent_product_id ? (
              <div className="text-center py-6">
                <div className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-violet-100 dark:bg-violet-900/30 text-violet-500 dark:text-violet-400 mb-4">
                  <KpiIcon d="M4 6h16M4 10h16M4 14h16M4 18h16" className="h-7 w-7" />
                </div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">Variant Child</p>
                <p className="text-xs text-neutral-500 max-w-sm mx-auto">
                  This product is a variant of another product. Edit the parent to manage the matrix.
                </p>
              </div>
            ) : (
              <div className="py-2" data-testid="variant-matrix-panel">
                {/* Existing variants — previously there was no surface listing
                    them, so a created variant could not be found or edited. */}
                {variantList.length > 0 && (
                  <div className="mb-6">
                    <SectionHeader title="Variants" tone="violet" />
                    <div className="mt-3">
                      <VariantPricingTable
                        variants={variantList}
                        currency={currency}
                        canEditPrices={userRoleLevel >= ROLE_LEVELS.OWNER}
                        saving={variantPricesSaving}
                        onSave={saveVariantPrices}
                        onToggleActive={(variantId, is_active) => variantActiveMutation.mutate({ variantId, is_active })}
                        togglingActive={variantActiveMutation.isPending}
                        onEdit={(variantId) => confirmLeave(() => navigate(`/products/${id}/variants/${variantId}`))}
                        onDirtyChange={setVariantPricesDirty}
                      />
                    </div>
                  </div>
                )}

                <SectionHeader title="Variant Matrix" tone="violet" />
                <p className="text-xs text-neutral-500 mb-4">
                  {variantList.length > 0
                    ? `Add more combinations of ${lockedAttributeNames.join(' × ') || 'the existing attributes'} — each gets its own SKU, barcode, price, cost and stock.`
                    : 'Pick attributes from your library (or add new ones) and generate every combination as child variants with their own SKU, barcode, price, cost and stock.'}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={openVariantMatrix}
                  data-testid="open-variant-matrix"
                >
                  {variantList.length > 0 ? 'Add Variants' : 'Configure Variant Matrix'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ─── Website Tab ─── */}
        {activeTab === 'website' && (
          <div className={cn(GLASS_FORM, 'p-6')}>
            <SectionHeader title="Photo gallery" tone="violet" />
            <ProductGalleryPanel productId={id!} />

            <div className="mt-8">
              <SectionHeader title="Merchandising" tone="brand" />
              <ProductMerchandisingPanel product={product} />
            </div>
          </div>
        )}
      </div>

      {/* ─── Modals ──────────────────────────────────────────────── */}
      {leaveDialog}
      {showStockAdjust && (
        <Modal open title="Adjust Stock" onClose={() => { setShowStockAdjust(false); setAdjustError(''); }}>
          <div className="space-y-4 p-4">
            {adjustError && (
              <p role="alert" className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/30 dark:text-danger-300">
                {adjustError}
              </p>
            )}
            {/* Products are location-scoped rows sharing a SKU, so the target
                location must be explicit — otherwise the adjustment lands on
                whichever row the URL happens to name. Only shown when the SKU is
                actually stocked at more than one location. */}
            {(stockData?.length ?? 0) > 1 && (
              <FormField label="Location" required>
                <Select
                  options={(stockData ?? []).map((s) => ({
                    label: `${s.location_name} — ${s.stock_quantity} in stock`,
                    value: s.location_id,
                  }))}
                  value={adjustLocationId}
                  onChange={(v) => setAdjustLocationId(typeof v === 'string' ? v : '')}
                  placeholder="Select location"
                />
              </FormField>
            )}
            <FormField label="Adjustment Type" required>
              <Select
                options={ADJUSTMENT_TYPE_OPTIONS}
                value={adjustType}
                onChange={(v) => setAdjustType(typeof v === 'string' ? v as StockAdjustmentType | 'COUNT' : '')}
                placeholder="Select adjustment type"
              />
            </FormField>
            <FormField
              label={isCount ? 'Counted Quantity' : 'Quantity'}
              required
              helperText={isCount ? 'Type the quantity you counted on the shelf. The difference from the system stock is booked as an adjustment.' : undefined}
            >
              <Input type="number" value={adjustQty} onChange={setAdjustQty} placeholder={isCount ? 'e.g. 25' : 'e.g. 10'} />
            </FormField>
            {adjustType === 'PURCHASE' && (
              <FormField label={`Unit Cost (${getCurrencySymbol(currency)})`} required>
                <Input type="number" variant="currency" currency={currency} value={adjustUnitCost} onChange={setAdjustUnitCost} placeholder="e.g. 350" />
              </FormField>
            )}
            {isWriteOffType && (openBatches?.length ?? 0) > 0 && (
              <FormField label="Batch" helperText="Leave blank to write off the soonest-expiring batches first (FEFO).">
                <Select
                  options={[
                    { label: 'Auto — soonest expiry first', value: '' },
                    ...(openBatches ?? []).map((b) => ({
                      label: `${b.batch_number} · exp ${b.expiry_date} · ${b.quantity} in stock`,
                      value: b.id,
                    })),
                  ]}
                  value={adjustBatchId}
                  onChange={(v) => setAdjustBatchId(typeof v === 'string' ? v : '')}
                  placeholder="Auto — soonest expiry first"
                />
              </FormField>
            )}
            <FormField label="Notes">
              <Input value={adjustNotes} onChange={setAdjustNotes} placeholder="Optional notes" />
            </FormField>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => { setShowStockAdjust(false); setAdjustError(''); }}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => { setAdjustError(''); stockAdjustMutation.mutate(); }}
                disabled={
                  !adjustType || !adjustQty ||
                  ((stockData?.length ?? 0) > 1 && !adjustLocationId) ||
                  (adjustType === 'PURCHASE' && !adjustUnitCost) ||
                  stockAdjustMutation.isPending
                }
              >
                {stockAdjustMutation.isPending ? 'Adjusting…' : 'Adjust Stock'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showVariantMatrix && (
        <Modal
          open
          variant="xl"
          title={variantList.length > 0 ? 'Add Variants' : 'Configure Variant Matrix'}
          onClose={() => setShowVariantMatrix(false)}
        >
          <div className="space-y-4 p-4" data-testid="variant-matrix-modal">
            <VariantMatrixBuilder
              attributes={variantAttrs}
              onAttributesChange={setVariantAttrs}
              variants={variantRows}
              onVariantsChange={setVariantRows}
              library={variantLibrary ?? []}
              lockedAttributeNames={lockedAttributeNames}
              existingCombinationKeys={existingCombinationKeys}
              parentSku={product.sku}
              parentPrice={product.price}
              parentCost={product.cost}
              currency={currency}
              error={variantError}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowVariantMatrix(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleVariantSave}
                disabled={
                  buildCreateVariantsPayload(variantRows, currency).variants.length === 0 || variantCreateMutation.isPending
                }
                data-testid="generate-variant-matrix"
              >
                {variantCreateMutation.isPending
                  ? 'Creating…'
                  : `Create ${buildCreateVariantsPayload(variantRows, currency).variants.length} Variants`}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showDeactivate && (
        <ConfirmDialog
          open
          title={masterWide ? 'Deactivate Product' : `Stop selling at ${storeName}`}
          description={!masterWide
            ? `This will stop selling "${product.name}"${product.has_variants ? ' and its variants' : ''} at ${storeName} only (POS, sales, quotations). Other stores keep selling it. It can be reactivated later.`
            : product.has_variants
              ? `This will deactivate "${product.name}" AND all its variants everywhere (POS, sales, quotations). When you reactivate it, variants you had switched off yourself stay off.`
              : `This will hide "${product.name}" everywhere (POS, sales, quotations). It can be reactivated later.`}
          confirmLabel="Deactivate"
          variant="danger"
          loading={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate()}
          onCancel={() => setShowDeactivate(false)}
        />
      )}

    </PageContainer>
  );
}

/**
 * What the shop leads with.
 *
 * `is_bestseller` and `display_order` have been on `products` with an index
 * since the menu work and they drive the storefront's default `featured` sort —
 * with no UI anywhere, so a shopkeeper could not choose what their shop leads
 * with. This is that UI; there is no schema work behind it.
 *
 * The fields are read off the product with a cast because the shared `Product`
 * type does not declare them, although `findById` selects `p.*` and returns
 * them. Widening the shared type is a cross-app change for two optional fields
 * and belongs in its own commit.
 */
function ProductMerchandisingPanel({ product }: { product: { id: string } }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const row = product as { id: string; is_bestseller?: boolean; display_order?: number };

  const [bestseller, setBestseller] = useState(Boolean(row.is_bestseller));
  const [order, setOrder] = useState(String(row.display_order ?? 0));

  const save = useMutation({
    mutationFn: () =>
      merchandisingService.update(row.id, {
        is_bestseller: bestseller,
        display_order: Number.isFinite(Number(order)) ? Math.max(0, Number(order)) : 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', row.id] });
      toast({ title: 'Saved', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message || 'Could not save', variant: 'error' }),
  });

  return (
    <div className="space-y-4">
      <Toggle
        checked={bestseller}
        onChange={setBestseller}
        label="Show as popular on the website"
        id="prod-bestseller"
      />
      <p className="-mt-2 text-xs text-neutral-500 dark:text-neutral-400">
        Also lifts it in the default order customers see.
      </p>

      <FormField
        label="Position"
        htmlFor="prod-display-order"
        helperText="Lower comes first within its category. Leave at 0 unless you want to pin something."
      >
        <Input
          id="prod-display-order"
          type="number"
          min={0}
          value={order}
          onChange={setOrder}
        />
      </FormField>

      <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save merchandising'}
      </Button>
    </div>
  );
}

export function ProductDetailPage() {
  return (
    <ErrorBoundary>
      <ProductDetailContent />
    </ErrorBoundary>
  );
}
